import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Card,
  IconButton,
  Autocomplete,
} from '@mui/material';
import {
  Add,
  Close,
  Person,
  People,
  Delete,
  CheckCircle,
  Warning,
  InfoOutlined,
} from '@mui/icons-material';
import { createPaymentLink, createSplitPaymentLinks } from '../../services/paymentLinkService';
import { searchCustomers, getOrCreateCustomer } from '../../services/customerService';
import { CustomerSearchResult } from '../../types/Customer';
import {
  validateEmail,
  validatePhoneSimple,
  validateAmount,
  validateDepositAmount,
  validateDueDates,
  validateInvoiceNumber,
  formatCurrency,
} from '../../utils/validators';
import { toast } from 'react-toastify';
import CopyLinkButton from '../common/CopyLinkButton';
import CurrencySelect from '../common/CurrencySelect';
import { Currency } from '../../types/PaymentLink';
import { getCurrentUser } from '../../services/authService';
import { buildPaymentUrl } from '../../config/urls';
import { getTodayForDateInput } from '../../utils/dateUtils';

// Default due dates for a brand-new link. The invoice deadline follows the app
// convention of starting from today. The deposit deadline defaults to the same
// day, so the "deposit due <= invoice due" rule always holds out of the box.
const getDefaultDueDates = (): { depositDueDate: Date; invoiceDueDate: Date } => {
  const invoiceDueDate = getTodayForDateInput();
  return { invoiceDueDate, depositDueDate: new Date(invoiceDueDate) };
};

interface CreateLinkDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  invoiceNumber: string;
  invoiceAmount: string;
  depositAmount: string;
  currency: Currency;
  description: string;
  depositDueDate: Date | null;
  invoiceDueDate: Date | null;
  allowPartialPayment: boolean;
  sendEmailToCustomer: boolean;
}

interface Payer {
  name: string;
  email: string;
  amount: number;
  depositAmount?: number;
}

interface FormErrors {
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  invoiceNumber?: string;
  invoiceAmount?: string;
  depositAmount?: string;
  description?: string;
  depositDueDate?: string;
  invoiceDueDate?: string;
  payers?: string;
}

const CreateLinkDialog: React.FC<CreateLinkDialogProps> = ({ open, onClose, onSuccess }) => {
  const todayDateInputValue = getTodayForDateInput().toISOString().split('T')[0];
  const [paymentType, setPaymentType] = useState<'standard' | 'split'>('standard');
  const [formData, setFormData] = useState<FormData>(() => ({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    invoiceNumber: '',
    invoiceAmount: '',
    depositAmount: '',
    currency: 'EUR',
    description: '',
    ...getDefaultDueDates(), // Deposit due defaults to the invoice due date (same day)
    allowPartialPayment: true, // Default: enabled
    sendEmailToCustomer: false, // Default: disabled (user must opt-in)
  }));

  const [payers, setPayers] = useState<Payer[]>([
    { name: '', email: '', amount: 0, depositAmount: undefined },
    { name: '', email: '', amount: 0, depositAmount: undefined },
  ]);

  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [createdLinks, setCreatedLinks] = useState<string[] | null>(null);

  // Customer autocomplete state
  const [customerOptions, setCustomerOptions] = useState<CustomerSearchResult[]>([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerSearchResult | null>(null);

  // Refresh the due-date defaults every time the dialog opens so they never go stale
  // (the dialog stays mounted between opens). Keeps deposit due == invoice due by default.
  useEffect(() => {
    if (open) {
      setFormData((prev) => ({ ...prev, ...getDefaultDueDates() }));
    }
  }, [open]);

  // Auto-calculate payer amounts when invoice amount or payer count changes
  useEffect(() => {
    if (paymentType === 'split' && formData.invoiceAmount) {
      const invoiceAmount = parseFloat(formData.invoiceAmount);
      if (!isNaN(invoiceAmount) && invoiceAmount > 0) {
        const perPerson = invoiceAmount / payers.length;
        const newPayers = payers.map((payer, index) => ({
          ...payer,
          amount: index === payers.length - 1
            ? Math.round((invoiceAmount - (perPerson * (payers.length - 1))) * 100) / 100 // Last payer gets remainder
            : Math.round(perPerson * 100) / 100,
        }));
        setPayers(newPayers);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentType, formData.invoiceAmount, payers.length]);

  // Calculate payer totals
  const payersTotal = payers.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const invoiceAmountNum = parseFloat(formData.invoiceAmount) || 0;
  const totalMatches = Math.abs(payersTotal - invoiceAmountNum) < 0.01;

  const handleChange = (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });

    // Clear selected customer if manually editing
    if (field === 'customerName' || field === 'customerEmail') {
      setSelectedCustomer(null);
    }

    // Clear error when user starts typing (only for fields with errors)
    if (field !== 'currency' && field !== 'allowPartialPayment' && field !== 'sendEmailToCustomer' && errors[field as keyof FormErrors]) {
      setErrors({
        ...errors,
        [field]: undefined,
      });
    }
  };

  // Customer autocomplete search (debounced)
  const handleCustomerSearch = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setCustomerOptions([]);
      return;
    }

    setCustomerSearchLoading(true);
    try {
      const results = await searchCustomers(searchTerm);
      setCustomerOptions(results);
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  // Customer selected from autocomplete
  const handleCustomerSelect = (customer: CustomerSearchResult | null) => {
    setSelectedCustomer(customer);
    if (customer) {
      setFormData({
        ...formData,
        customerName: customer.name,
        customerEmail: customer.email,
        customerPhone: customer.phone || '',
      });
    }
  };

  const handleCheckboxChange = (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.checked,
    });
  };

  const handleDateChange = (field: 'depositDueDate' | 'invoiceDueDate') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const dateValue = event.target.value ? new Date(event.target.value) : null;
    setFormData((prev) => {
      const next: FormData = { ...prev, [field]: dateValue };

      // Enforce the rule: the deposit deadline can never fall AFTER the invoice
      // deadline. Whether the user moves the invoice date earlier or sets the
      // deposit date later, pull the deposit back to the invoice due date.
      if (
        next.depositDueDate &&
        next.invoiceDueDate &&
        next.depositDueDate.getTime() > next.invoiceDueDate.getTime()
      ) {
        next.depositDueDate = next.invoiceDueDate;
      }

      return next;
    });

    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: undefined,
      });
    }

    // The deposit due date may have been auto-adjusted above; clear any stale error.
    if (field === 'invoiceDueDate' && errors.depositDueDate) {
      setErrors((prev) => ({ ...prev, depositDueDate: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validate based on payment type
    if (paymentType === 'standard') {
      // Validate customer name
      if (!formData.customerName.trim()) {
        newErrors.customerName = 'Customer name is required';
      } else if (formData.customerName.trim().length < 2) {
        newErrors.customerName = 'Name must be at least 2 characters';
      }

      // Validate email
      if (!formData.customerEmail.trim()) {
        newErrors.customerEmail = 'Email is required';
      } else if (!validateEmail(formData.customerEmail)) {
        newErrors.customerEmail = 'Invalid email format';
      }

      // Validate deposit amount (now optional - only validate if provided)
      const depositTrimmed = formData.depositAmount.trim();
      if (depositTrimmed) {
        const depositNum = parseFloat(depositTrimmed);
        const invoiceNum = parseFloat(formData.invoiceAmount);
        if (isNaN(depositNum) || depositNum < 0) {
          newErrors.depositAmount = 'Deposit amount must be a non-negative number';
        } else if (depositNum > invoiceNum) {
          newErrors.depositAmount = 'Deposit amount cannot exceed invoice amount';
        } else if (depositNum > 0 && !isNaN(invoiceNum) && !validateDepositAmount(depositNum, invoiceNum)) {
          newErrors.depositAmount = 'Deposit amount cannot exceed invoice amount';
        }
      }
    } else {
      // Split payment validation
      
      // Validate invoice owner (parent customer)
      if (!formData.customerName.trim()) {
        newErrors.customerName = 'Invoice owner name is required';
      } else if (formData.customerName.trim().length < 2) {
        newErrors.customerName = 'Name must be at least 2 characters';
      }

      // Validate invoice owner email
      if (!formData.customerEmail.trim()) {
        newErrors.customerEmail = 'Invoice owner email is required';
      } else if (!validateEmail(formData.customerEmail)) {
        newErrors.customerEmail = 'Invalid email format';
      }
      
      if (!totalMatches) {
        newErrors.payers = 'Payer amounts must equal invoice amount';
      }

      // Validate payers
      for (let i = 0; i < payers.length; i++) {
        const payer = payers[i];
        console.log(`[VALIDATION] Payer ${i + 1}:`, payer);
        if (!payer.name.trim() || !payer.email.trim() || payer.amount <= 0) {
          newErrors.payers = 'All payer fields must be filled';
          console.log(`[VALIDATION] Payer ${i + 1} validation failed: incomplete fields`);
          break;
        }
        if (!validateEmail(payer.email)) {
          newErrors.payers = `Invalid email for payer ${i + 1}`;
          console.log(`[VALIDATION] Payer ${i + 1} validation failed: invalid email`);
          break;
        }
      }

      // Invoice number is required for split payments
      if (!formData.invoiceNumber.trim()) {
        newErrors.invoiceNumber = 'Invoice number is required for split payments';
      }
    }

    // Validate phone (optional for both types)
    if (formData.customerPhone.trim() && !validatePhoneSimple(formData.customerPhone)) {
      newErrors.customerPhone = 'Invalid phone format (10-15 digits required)';
    }

    // Validate invoice number format (if provided)
    if (formData.invoiceNumber.trim() && !validateInvoiceNumber(formData.invoiceNumber)) {
      newErrors.invoiceNumber = 'Invalid invoice number format (alphanumeric, dashes, slashes only)';
    }

    // Validate invoice amount
    if (!formData.invoiceAmount.trim()) {
      newErrors.invoiceAmount = 'Invoice amount is required';
    } else {
      const amountNum = parseFloat(formData.invoiceAmount);
      if (isNaN(amountNum) || !validateAmount(amountNum)) {
        newErrors.invoiceAmount = 'Invoice amount must be a positive number';
      }
    }

    // Validate description (optional)
    if (formData.description.trim() && formData.description.trim().length < 5) {
      newErrors.description = 'Description must be at least 5 characters';
    }

    // Check if deposit amount is provided
    const depositTrimmed = formData.depositAmount.trim();
    const hasDeposit = depositTrimmed && parseFloat(depositTrimmed) > 0;

    // Validate deposit due date (only if deposit is provided)
    if (hasDeposit && !formData.depositDueDate) {
      newErrors.depositDueDate = 'Deposit due date is required when deposit amount is specified';
    }

    // Validate invoice due date
    if (!formData.invoiceDueDate) {
      newErrors.invoiceDueDate = 'Invoice due date is required';
    }

    // Validate due dates relationship (only if deposit is provided)
    if (hasDeposit && formData.depositDueDate && formData.invoiceDueDate) {
      if (!validateDueDates(formData.depositDueDate, formData.invoiceDueDate)) {
        newErrors.invoiceDueDate = 'Invoice due date must be after or equal to deposit due date';
      }
    }

    setErrors(newErrors);
    
    // Log validation errors for debugging
    if (Object.keys(newErrors).length > 0) {
      console.log('[VALIDATION] Failed with errors:', newErrors);
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    console.log('[CREATE-LINK] Starting form submission...');
    console.log('[CREATE-LINK] Payment type:', paymentType);
    console.log('[CREATE-LINK] Form data:', formData);
    console.log('[CREATE-LINK] Payers:', payers);
    
    if (!validateForm()) {
      console.log('[CREATE-LINK] Validation failed. Errors:', errors);
      return;
    }
    
    console.log('[CREATE-LINK] Validation passed. Creating payment link...');
    setLoading(true);

    try {
      const currentUser = getCurrentUser();

      // Get or create customer record
      const customerId = await getOrCreateCustomer(
        formData.customerEmail.trim(),
        formData.customerName.trim(),
        formData.customerPhone?.trim(),
        currentUser?.email || undefined
      );

      if (paymentType === 'split') {
        // Create split payment
        // Deposit due date - use if any payer has deposit
        const hasAnyDeposit = payers.some(p => p.depositAmount && p.depositAmount > 0);
        const depositDueDate = hasAnyDeposit && formData.depositDueDate ? formData.depositDueDate : undefined;

        const result = await createSplitPaymentLinks({
          customerId, // Add customer ID reference
          customerName: formData.customerName.trim(), // Invoice owner name
          customerEmail: formData.customerEmail.trim(), // Invoice owner email
          invoiceNumber: formData.invoiceNumber.trim(),
          invoiceAmount: parseFloat(formData.invoiceAmount),
          depositDueDate: depositDueDate,
          currency: formData.currency,
          description: formData.description.trim() || 'Split Payment',
          invoiceDueDate: formData.invoiceDueDate!,
          allowPartialPayment: formData.allowPartialPayment,
          sendEmailToCustomer: formData.sendEmailToCustomer, // Add email flag
          payers: payers.map(p => ({
            name: p.name.trim(),
            email: p.email.trim(),
            amount: p.amount,
            depositAmount: p.depositAmount,
          })),
          createdBy: currentUser?.email || 'admin@alexatur.com',
          customerPhone: formData.customerPhone?.trim() || '',
        });

        const childLinks = result.childLinks.map((child: any) =>
          buildPaymentUrl(`/pay/${child.linkId}`)
        );
        setCreatedLinks(childLinks);
        toast.success(`Split payment created! ${result.childIds.length} links generated`);
      } else {
        // Standard link creation (existing logic)
        // Handle deposit amount: treat empty or 0 as "no deposit"
        const depositValue = formData.depositAmount.trim()
          ? parseFloat(formData.depositAmount)
          : 0;

        const inputData: any = {
          customerId, // Add customer ID reference
          customerName: formData.customerName.trim(),
          customerEmail: formData.customerEmail.trim(),
          customerPhone: formData.customerPhone?.trim() || '',
          invoiceAmount: parseFloat(formData.invoiceAmount),
          depositAmount: depositValue,
          currency: formData.currency,
          description: formData.description.trim() || 'Payment Link',
          depositDueDate: formData.depositDueDate!,
          invoiceDueDate: formData.invoiceDueDate!,
          allowPartialPayment: formData.allowPartialPayment,
          sendEmailToCustomer: formData.sendEmailToCustomer, // Add email flag
          createdBy: currentUser?.email || 'admin@alexatur.com',
          type: 'link' as const,
        };

        // Only include invoice number if it has a value
        const invoiceNum = formData.invoiceNumber.trim();
        if (invoiceNum) {
          inputData.invoiceNumber = invoiceNum;
        }

        const paymentLink = await createPaymentLink(inputData);
        setCreatedLinks([paymentLink.linkUrl]);
        toast.success('Payment link created successfully!');
      }

      onSuccess();
    } catch (error: any) {
      console.error('Error creating payment link:', error);
      toast.error(error.message || 'Failed to create payment link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setPaymentType('standard');
      setFormData({
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        invoiceNumber: '',
        invoiceAmount: '',
        depositAmount: '',
        currency: 'EUR',
        description: '',
        ...getDefaultDueDates(), // Deposit due defaults to the invoice due date (same day)
        allowPartialPayment: true,
        sendEmailToCustomer: false, // Add default value
      });
      setPayers([
        { name: '', email: '', amount: 0 },
        { name: '', email: '', amount: 0 },
      ]);
      setErrors({});
      setCreatedLinks(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Add />
        <Typography variant="h6" component="span">
          Create New Payment Link
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {createdLinks ? (
          <Box>
            <Alert
              severity="success"
              sx={{
                mb: 3,
                backgroundColor: '#ffffff !important',
                color: '#000000 !important',
                borderLeft: '4px solid !important',
                borderLeftColor: '#4caf50 !important',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15) !important',
                fontWeight: '600 !important',
                fontSize: '14px !important',
                '& .MuiAlert-icon': {
                  color: '#4caf50 !important',
                },
                '& .MuiAlert-message': {
                  color: '#000000 !important',
                  fontWeight: '600 !important',
                },
              }}
            >
              {paymentType === 'split'
                ? `Split payment created successfully! ${createdLinks.length} links generated.`
                : 'Payment link created successfully!'}
            </Alert>

            {createdLinks.map((link, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 2,
                }}
              >
                {paymentType === 'split' && (
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Payer {index + 1}: {payers[index]?.name} ({payers[index]?.email})
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    wordBreak: 'break-all',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                  }}
                >
                  {link}
                </Typography>
                <CopyLinkButton linkUrl={link} size="small" fullWidth />
              </Box>
            ))}
          </Box>
        ) : (
          <Grid container spacing={2.5}>
            {/* Payment Type Toggle */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Payment Type
              </Typography>
              <ToggleButtonGroup
                value={paymentType}
                exclusive
                onChange={(_e, newValue) => {
                  if (newValue !== null) {
                    setPaymentType(newValue);
                    setErrors({});
                    if (newValue === 'split') {
                      setPayers([
                        { name: '', email: '', amount: 0 },
                        { name: '', email: '', amount: 0 },
                      ]);
                    }
                  }
                }}
                fullWidth
                sx={{ mb: 1 }}
              >
                <ToggleButton value="standard">
                  <Person sx={{ mr: 1 }} />
                  Standard Link
                </ToggleButton>
                <ToggleButton value="split">
                  <People sx={{ mr: 1 }} />
                  Split Payment
                </ToggleButton>
              </ToggleButtonGroup>

              {paymentType === 'standard' && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Single payer, can pay in multiple installments
                </Alert>
              )}

              {paymentType === 'split' && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Multiple payers, each gets their own unique payment link
                </Alert>
              )}
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Customer Information - For both Standard and Split */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                {paymentType === 'split' ? 'Invoice Owner Information' : 'Customer Information'}
              </Typography>
              {paymentType === 'split' && (
                <Typography variant="caption" color="text.secondary">
                  The main contact who owns this invoice (parent record)
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={customerOptions}
                loading={customerSearchLoading}
                value={selectedCustomer}
                onChange={(_, newValue) => {
                  if (typeof newValue === 'object' && newValue) {
                    handleCustomerSelect(newValue);
                  }
                }}
                onInputChange={(_, newInputValue) => {
                  // Update form data when typing
                  setFormData({ ...formData, customerName: newInputValue });
                  // Clear selected if manually typing
                  if (selectedCustomer && newInputValue !== selectedCustomer.name) {
                    setSelectedCustomer(null);
                  }
                  // Search customers
                  if (newInputValue.length >= 2) {
                    handleCustomerSearch(newInputValue);
                  }
                }}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.name;
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                        {option.phone && ` • ${option.phone}`}
                        {option.totalPayments > 0 && ` • ${option.totalPayments} payment${option.totalPayments > 1 ? 's' : ''}`}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={paymentType === 'split' ? 'Invoice Owner Name' : 'Customer Name'}
                    required
                    error={!!errors.customerName}
                    helperText={errors.customerName || 'Start typing to search existing customers'}
                    disabled={loading}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {customerSearchLoading ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={paymentType === 'split' ? 'Invoice Owner Email' : 'Email'}
                type="email"
                value={formData.customerEmail}
                onChange={handleChange('customerEmail')}
                error={!!errors.customerEmail}
                helperText={errors.customerEmail}
                required
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number (Optional)"
                value={formData.customerPhone}
                onChange={handleChange('customerPhone')}
                error={!!errors.customerPhone}
                helperText={errors.customerPhone || 'Optional - Any format, 10-15 digits'}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            {/* Invoice & Payment Details Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Invoice & Payment Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Invoice Number"
                value={formData.invoiceNumber}
                onChange={handleChange('invoiceNumber')}
                error={!!errors.invoiceNumber}
                helperText={errors.invoiceNumber || (paymentType === 'split' ? 'Required (e.g., INV-2025-001)' : 'Optional (e.g., INV-2025-001)')}
                required={paymentType === 'split'}
                disabled={loading}
                placeholder="INV-2025-001"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <CurrencySelect
                value={formData.currency}
                onChange={(currency) => setFormData({ ...formData, currency })}
                disabled={loading}
                required
              />
            </Grid>

            {/* Invoice Amount */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Invoice Amount"
                type="number"
                value={formData.invoiceAmount}
                onChange={handleChange('invoiceAmount')}
                error={!!errors.invoiceAmount}
                helperText={errors.invoiceAmount || (paymentType === 'split' ? 'Total to be split among payers' : 'Total invoice amount')}
                required
                disabled={loading}
                inputProps={{
                  min: 1,
                  step: 0.01,
                }}
                onWheel={(e) => (e.target as HTMLElement).blur()}
              />
            </Grid>

            {/* Invoice Due Date - Main deadline */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Invoice Due Date"
                type="date"
                value={formData.invoiceDueDate ? formData.invoiceDueDate.toISOString().split('T')[0] : ''}
                onChange={handleDateChange('invoiceDueDate')}
                error={!!errors.invoiceDueDate}
                helperText={errors.invoiceDueDate || 'Deadline for full payment'}
                required
                disabled={loading}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: todayDateInputValue,
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  Defaults to today. Adjust the date if needed.
                </Typography>
              </Box>
            </Grid>

            {/* Deposit Amount - only for standard payments, split payments use per-payer deposits */}
            {paymentType !== 'split' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Deposit Amount (Optional)"
                  type="number"
                  value={formData.depositAmount}
                  onChange={handleChange('depositAmount')}
                  error={!!errors.depositAmount}
                  helperText={errors.depositAmount || 'Leave empty or 0 for no deposit requirement'}
                  disabled={loading}
                  inputProps={{
                    min: 0,
                    step: 0.01,
                  }}
                  onWheel={(e) => (e.target as HTMLElement).blur()}
                />
              </Grid>
            )}

            {/* Deposit Due Date - shown when deposit amount is entered */}
            {formData.depositAmount && parseFloat(formData.depositAmount) > 0 && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Deposit Due Date"
                  type="date"
                  value={formData.depositDueDate ? formData.depositDueDate.toISOString().split('T')[0] : ''}
                  onChange={handleDateChange('depositDueDate')}
                  error={!!errors.depositDueDate}
                  helperText={errors.depositDueDate || 'Deadline for deposit payment'}
                  required
                  disabled={loading}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: todayDateInputValue,
                    // Deposit can never be due after the invoice deadline.
                    max: formData.invoiceDueDate
                      ? formData.invoiceDueDate.toISOString().split('T')[0]
                      : undefined,
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    Defaults to the invoice due date — set an earlier date if the deposit is due sooner.
                  </Typography>
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (Optional)"
                value={formData.description}
                onChange={handleChange('description')}
                error={!!errors.description}
                helperText={errors.description || 'Brief description of the payment'}
                disabled={loading}
                multiline
                rows={3}
                placeholder="Golf reservation, Green fee, Hotel room booking, etc."
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.allowPartialPayment}
                    onChange={handleCheckboxChange('allowPartialPayment')}
                    disabled={loading}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Allow Partial Payments</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {paymentType === 'split'
                        ? 'Each payer can pay their share in multiple installments'
                        : 'Customer can pay in multiple installments'}
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.sendEmailToCustomer}
                    onChange={handleCheckboxChange('sendEmailToCustomer')}
                    disabled={loading}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Send Payment Link via Email</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {paymentType === 'split'
                        ? 'Send payment link emails to all payers'
                        : 'Send payment link email to customer'}
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            {/* Split Payment Payers Section */}
            {paymentType === 'split' && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Payers
                  </Typography>
                  {errors.payers && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {errors.payers}
                    </Alert>
                  )}
                </Grid>

                {payers.map((payer, index) => (
                  <Grid item xs={12} key={index}>
                    <Card variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2" fontWeight={600}>
                          Payer {index + 1}
                        </Typography>
                        {payers.length > 2 && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              const newPayers = payers.filter((_, i) => i !== index);
                              setPayers(newPayers);
                            }}
                            disabled={loading}
                          >
                            <Delete />
                          </IconButton>
                        )}
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            label="Full Name"
                            value={payer.name}
                            onChange={(e) => {
                              const newPayers = [...payers];
                              newPayers[index].name = e.target.value;
                              setPayers(newPayers);
                            }}
                            required
                            size="small"
                            disabled={loading}
                          />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={payer.email}
                            onChange={(e) => {
                              const newPayers = [...payers];
                              newPayers[index].email = e.target.value;
                              setPayers(newPayers);
                            }}
                            required
                            size="small"
                            disabled={loading}
                          />
                        </Grid>

                        <Grid item xs={12} sm={2}>
                          <TextField
                            fullWidth
                            label="Amount"
                            type="text"
                            value={payer.amount || ''}
                            onChange={(e) => {
                              const newPayers = [...payers];
                              // Sanitize input: remove invalid characters, allow only numbers, comma, and dot
                              let value = e.target.value.replace(/[^0-9.,]/g, '');
                              // Normalize comma to dot
                              value = value.replace(/,/g, '.');
                              // Parse to number
                              const numValue = value === '' ? 0 : parseFloat(value);
                              newPayers[index].amount = isNaN(numValue) ? 0 : numValue;
                              setPayers(newPayers);
                            }}
                            required
                            size="small"
                            disabled={loading}
                            placeholder="0.00"
                          />
                        </Grid>

                        <Grid item xs={12} sm={2}>
                          <TextField
                            fullWidth
                            label="Deposit (Opt.)"
                            type="text"
                            value={payer.depositAmount || ''}
                            onChange={(e) => {
                              const newPayers = [...payers];
                              // Sanitize input
                              let value = e.target.value.replace(/[^0-9.,]/g, '');
                              value = value.replace(/,/g, '.');
                              const numValue = value === '' ? undefined : parseFloat(value);
                              newPayers[index].depositAmount = (numValue && !isNaN(numValue)) ? numValue : undefined;
                              setPayers(newPayers);
                            }}
                            size="small"
                            disabled={loading}
                            placeholder="0.00"
                            helperText="Optional"
                          />
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <Button
                    startIcon={<Add />}
                    onClick={() => {
                      setPayers([...payers, { name: '', email: '', amount: 0, depositAmount: undefined }]);
                    }}
                    variant="outlined"
                    fullWidth
                    disabled={loading}
                  >
                    Add Another Payer
                  </Button>
                </Grid>

                {/* Validation Display */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      bgcolor: totalMatches ? 'success.light' : 'warning.light',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        Total from payers: {formatCurrency(payersTotal, formData.currency)}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        Invoice amount: {formatCurrency(invoiceAmountNum, formData.currency)}
                      </Typography>
                      {totalMatches ? (
                        <Chip icon={<CheckCircle />} label="Amounts Match" color="success" size="small" />
                      ) : (
                        <Chip
                          icon={<Warning />}
                          label={`Difference: ${formatCurrency(Math.abs(payersTotal - invoiceAmountNum), formData.currency)}`}
                          color="warning"
                          size="small"
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {createdLinks ? (
          <Button onClick={handleClose} variant="contained" size="large" fullWidth>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={loading} startIcon={<Close />}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Add />}
            >
              {loading ? 'Creating...' : paymentType === 'split' ? 'Create Split Payment' : 'Create Link'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateLinkDialog;
